import { ReactNode, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Video as LucideIcon } from 'lucide-react';
import { UserSidebar } from './UserSidebar';
import { UserTopbar } from './UserTopbar';
import { USER_NAV_SECTIONS } from '../../config/userNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface UserLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  action?: {
    label: string;
    icon: LucideIcon;
    onClick: () => void;
  };
}

export function UserLayout({ children, title, description, action }: UserLayoutProps) {
  const location = useLocation();
  const { user, impersonatedUserId, stopImpersonation, effectiveUserId } = useAuth();
  const [pageTitle, setPageTitle] = useState(title || 'Tableau de bord');
  const [pageDescription, setPageDescription] = useState(description);
  const [userName, setUserName] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (title) return;

    for (const section of USER_NAV_SECTIONS) {
      const item = section.items.find(i => {
        if (i.path === '/app' && location.pathname === '/app') return true;
        if (i.path !== '/app' && location.pathname.startsWith(i.path)) return true;
        return false;
      });
      if (item) {
        setPageTitle(item.label);
        break;
      }
    }
  }, [location.pathname, title]);

  useEffect(() => {
    async function loadUserProfile() {
      if (!effectiveUserId) return;

      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('first_name, last_name')
          .eq('id', effectiveUserId)
          .maybeSingle();

        if (error) {
          console.error('Error loading user profile:', error);
          const fallback = user?.email?.split('@')[0] || 'Utilisateur';
          setUserName(fallback);
          return;
        }

        if (data?.first_name) {
          setUserName(data.first_name);
        } else {
          const fallback = user?.email?.split('@')[0] || 'Utilisateur';
          setUserName(fallback);
        }
      } catch (err) {
        console.error('Error loading user profile:', err);
        const fallback = user?.email?.split('@')[0] || 'Utilisateur';
        setUserName(fallback);
      }
    }

    loadUserProfile();
  }, [user, effectiveUserId]);

  async function handleStopImpersonation() {
    try {
      await stopImpersonation();
    } catch (error) {
      console.error('Error stopping impersonation:', error);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <UserSidebar
        collapsed={collapsed}
        onCollapse={setCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div className={`transition-all duration-300 ${collapsed ? 'md:pl-20' : 'md:pl-64'}`}>
        <UserTopbar
          title={pageTitle}
          description={pageDescription}
          action={action}
          userName={userName}
          showImpersonation={!!impersonatedUserId}
          onStopImpersonation={handleStopImpersonation}
          onMobileMenuOpen={() => setMobileOpen(true)}
        />

        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
